/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/contacts-view.tsx`.
 *
 * A REWRITE for the same reason as the sibling `accounts-view.tsx`: the Next
 * original was a server component that read `people` out of the vault inside its
 * render path (`shared-render-ownership.md` cause **A**). The tenant resolution,
 * `listPeople` and the self-person exclusion all moved behind
 * `crm.views.contacts`; the RENDER is verbatim.
 *
 * Changes, and only these:
 *   `import "server-only"`                  -> dropped (this host has no vault)
 *   `async function` + inline vault reads    -> `useApi(crm.views.contacts, {})`
 *   `listPeople(...)` rows                   -> `data.contacts` (same fields)
 *
 * The contract IS mounted on this host — this component is live, not a stub.
 */

import {
  DataTable,
  PageHeader,
  EmptyState,
  formatDate,
  ObjectLink,
} from "@excellent/ui";
import { Users } from "@excellent/ui/icons";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";

/**
 * Customers ▸ Contacts (Three-Layer PRD §5.8a — a REBUILD view, NOT an orphaned
 * mount). Contacts are PEOPLE in the sales context — distinct from Leads (leads ≠
 * all contacts), so there is no `LeadsView` to reuse. This is the net-new List
 * over `people`, the canonical Schema party that CRM/Hiring/the hub all
 * write. Each row opens its object page at `/o/<uid>` (people resolve via the
 * `DOMAIN_SPECS` person fallback). A `DataTable` per CLAUDE.md table rules (no
 * hand-rolled `<table>`); the page owns its `PageHeader`.
 *
 * `basePath` is accepted for the §5.5 view contract but contacts are
 * workspace-global parties.
 */
export function ContactsView(_props: { basePath: string }) {
  const { data, error, isLoading } = useApi(crm.views.contacts, {});
  const people = data?.contacts ?? [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Contacts"
        description="Every person across your accounts — buyers, champions, everyone in the room."
      />
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : people.length === 0 ? (
        <EmptyState
          icon={<Users className="size-5" />}
          title="No contacts yet"
          description="People appear here as leads, candidates, and synced counterparties are created."
        />
      ) : (
        <DataTable
          rows={people}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "name",
              header: "Name",
              render: (r) => (
                <ObjectLink uid={r.uid} from="customers" className="font-medium hover:underline">
                  {r.display_name}
                </ObjectLink>
              ),
            },
            {
              key: "given",
              header: "Given / family",
              render: (r) =>
                [r.given_name, r.family_name].filter(Boolean).join(" ") || "—",
            },
            {
              key: "created",
              header: "Added",
              render: (r) => formatDate(r.created_at),
            },
          ]}
        />
      )}
    </div>
  );
}
